import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import util from 'preact-util';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';

const toNumber = (val) => {
    if (val == null) return 0;
    if (typeof val === 'object' && val.$numberDecimal != null) return Number(val.$numberDecimal);
    if (typeof val === 'string') return Number(val.replace(',', '.')) || 0;
    return Number(val) || 0;
};

const formatCurrency = (n) => new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currency: 'NOK',
    maximumFractionDigits: 2
}).format(toNumber(n));

const formatNumber = (n) => new Intl.NumberFormat('nb-NO', {
    maximumFractionDigits: 2
}).format(toNumber(n));

const formatDateTime = (iso) => {
    if (!iso) return '-';
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return d.toLocaleString('nb-NO');
};

// sale line → computed display fields (percent per unit + flat per line, price assumed net ex. VAT)
const computeLine = (line) => {
    const qty = toNumber(line.product?.qty ?? line.qty);
    const price = toNumber(line.product?.price ?? line.price);
    const vatPct = toNumber(line.product?.vat ?? line.vat);
    const discountPct = toNumber(line.product?.discount ?? line.discount);
    const flatDiscount = toNumber(line.product?.discountAmount ?? line.discountAmount);

    const discountedUnit = price * (1 - discountPct / 100);
    let lineNet = qty * discountedUnit - flatDiscount;
    if (lineNet < 0) lineNet = 0;

    const lineVat = lineNet * (vatPct / 100);
    const lineGross = lineNet + lineVat;

    return {
        qty,
        price,
        vatPct,
        discountPct,
        flatDiscount,
        lineNet,
        lineVat,
        lineGross
    };
};

const flattenAllSales = (allSales) => {
    if (!Array.isArray(allSales)) return [];
    return allSales.flat ? allSales.flat(1) : [].concat(...allSales);
};

const fieldSorter = (fields) => (a, b) => fields.map(o => {
    let dir = 1;
    if (o[0] === '-') {
        dir = -1;
        o = o.substring(1);
    }
    return a[o] > b[o] ? dir : a[o] < b[o] ? -(dir) : 0;
}).reduce((p, n) => p ? p : n, 0);

@observer
class ProductPurchaseDetails extends Component {
    constructor(props) {
        super(props);
    }

    loadAll = async () => {
        const { id } = this.props;
        const { productStore } = this.props.stores;
        await productStore.loadPurchaseDetails(id);
    }

    componentDidMount() {
        this.loadAll();
    }

    componentDidUpdate() {
    }

    render() {
        const { id } = this.props;
        const { productStore, userStore } = this.props.stores;
        const { purchaseDetails, product } = productStore;

        const { user, isAdmin, customer } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const { productName } = purchaseDetails;
        const summary = purchaseDetails.summary || {};
        const timeline = purchaseDetails.timeline || [];
        const recentSales = purchaseDetails.recentSales || [];
        const versions = purchaseDetails.versions || [];

        // support multiple possible revenue keys (your sample had totRevenue)
        const revenue = toNumber(summary.grossRevenue ?? summary.netRevenue ?? summary.totRevenue ?? 0);
        const allSales = flattenAllSales(summary.allSales);

        // normalized rows to render
        let rows = allSales.sort(fieldSorter(['-saleDate'])).map((s) => {
            const saleId = s.saleId ?? s.id ?? s.saleID;
            const saleDate = s.saleDate ?? s.date;
            const productName = s.product?.name ?? s.name ?? '';
            const c = computeLine(s);
            return {
                saleId,
                saleDate,
                productName,
                ...c
            };
        });

        const stockChangeVersions = versions.filter(v => v.data && Object.keys(v.data).some(k => k === 'inStock'));

        // Push into rows
        stockChangeVersions.forEach(v => {
            rows.push({
                saleDate: v.createdDate,
                qty: v.data.inStock,
                ...v,
            });
        });

        rows = rows.sort(fieldSorter(['-saleDate', '-id']));

        return (
            <>
                <div class='w-100 d-flex justify-content-center position-relative'>
                    <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                        <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='d-flex align-items-center justify-content-between '>
                                <h5 class='m-0'>
                                    <Text id='product.purchase-details-title'>Product Purchase Details</Text>
                                    <span class='text-muted ms-2 font-weight-light'> <Text id='product.purchase-last-12-months'>Last 12 Months</Text></span>
                                </h5>
                                <span class='text-muted'>ID: {id}</span>
                            </div>
                            <span class='text-muted font-weight-light'> {productName}</span>

                            {/* Metric cards */}
                            <div class='d-flex flex-row mt-3'>
                                <div class='d-flex flex-column me-3 mb-2 p-2 rounded-lg shadow-sm' style='min-width: 160px;'>
                                    <div class='text-muted small'><Text id='product.inStock'>In Stock</Text></div>
                                    <div class='h5 m-0'>{formatNumber(product?.inStock || 0)}</div>
                                </div>
                            </div>
                            <div class='d-flex flex-row mb-3 mt-3'>
                                <div class='d-flex flex-column me-3 mb-2 p-2 rounded-lg shadow-sm' style='min-width: 160px;'>
                                    <div class='text-muted small'><Text id='product.purchase-details-orders'>Orders</Text></div>
                                    <div class='h5 m-0'>{formatNumber(summary.totalOrders || 0)}</div>
                                </div>
                                <div class='d-flex flex-column me-3 mb-2 p-2 rounded-lg shadow-sm' style='min-width: 160px;'>
                                    <div class='text-muted small'><Text id='product.purchase-details-units'>Units</Text></div>
                                    <div class='h5 m-0'>{formatNumber(summary.totalUnits || 0)}</div>
                                </div>
                                <div class='d-flex flex-column me-3 mb-2 p-2 rounded-lg shadow-sm' style='min-width: 200px;'>
                                    <div class='text-muted small'><Text id='product.purchase-details-revenue'>Revenue</Text></div>
                                    <div class='h5 m-0'>{formatCurrency(revenue)}</div>
                                </div>
                                <div class='d-flex flex-column me-3 mb-2 p-2 rounded-lg shadow-sm' style='min-width: 240px;'>
                                    <div class='text-muted small'><Text id='product.purchase-details-last-sale'>Last sale</Text></div>
                                    <div class='h6 m-0'>{util.formatDate(summary.lastSaleDate, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}</div>
                                </div>
                            </div>

                            {/* Sales table */}
                            <div class='table-responsive font-style-narrow'>
                                <table class='table table-sm mb-3'>
                                    <thead>
                                        <tr>
                                            <th style='white-space: nowrap;'><Text id='product.purchase-details-date'>Date</Text></th>
                                            <th style='white-space: nowrap;'><Text id='product.purchase-details-sale-id'>Sale ID</Text></th>
                                            <th><Text id='product.purchase-details-product'>Product</Text></th>
                                            <th class='text-end' style='white-space: nowrap;'><Text id='product.purchase-details-qty'>Qty</Text></th>
                                            <th class='text-end' style='white-space: nowrap;'><Text id='product.purchase-details-unit'>Unit</Text></th>
                                            <th class='text-end' style='white-space: nowrap;'><Text id='product.purchase-details-disc'>Disc %</Text></th>
                                            <th class='text-end' style='white-space: nowrap;'><Text id='product.purchase-details-flat-disc'>Flat Disc</Text></th>
                                            <th class='text-end' style='white-space: nowrap;'><Text id='product.purchase-details-net'>Net</Text></th>
                                            <th class='text-end' style='white-space: nowrap;'><Text id='product.purchase-details-vat-pct'>VAT %</Text></th>
                                            <th class='text-end' style='white-space: nowrap;'><Text id='product.purchase-details-vat'>VAT</Text></th>
                                            <th class='text-end' style='white-space: nowrap;'><Text id='product.purchase-details-gross'>Gross</Text></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length === 0 && (
                                            <tr>
                                                <td colSpan='11' class='text-muted'><Text id='product.purchase-details-no-sales'>No sales found for this product.</Text></td>
                                            </tr>
                                        )}
                                        {rows.map((r) => {
                                            if (!r.price) {
                                                return (<>
                                                    <tr key={`${r.saleId}-${r.saleDate}-${r.productName}`} class='table-success'>
                                                        <td style='white-space: nowrap;'>{util.formatDate(r.saleDate, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}</td>
                                                        <td style='white-space: nowrap;'>&nbsp;</td>
                                                        <td>
                                                            {r.data?.previous_inStock ? <>
                                                                <Text id='product.instock-adjusted-with'>In stock adjusted with</Text>
                                                            </> : <>
                                                                <Text id='product.instock-adjusted-to'>In stock adjusted to</Text>
                                                            </>}
                                                        </td>
                                                        <td class='text-end'>{r.data?.previous_inStock ? r.data?.inStock - r.data?.previous_inStock : r.data?.inStock}</td>
                                                        <td class='text-end'>&nbsp;</td>
                                                        <td class='text-end'>&nbsp;</td>
                                                        <td class='text-end'>&nbsp;</td>
                                                        <td class='text-end'>&nbsp;</td>
                                                        <td class='text-end'>&nbsp;</td>
                                                        <td class='text-end'>&nbsp;</td>
                                                        <td class='text-end font-weight-bold'>&nbsp;</td>
                                                    </tr>
                                                </>);
                                            }
                                            return (<>
                                                <tr key={`${r.saleId}-${r.saleDate}-${r.productName}`}>
                                                    <td style='white-space: nowrap;'>{util.formatDate(r.saleDate, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}</td>
                                                    <td style='white-space: nowrap;'>{r.saleId}</td>
                                                    <td>{r.productName}</td>
                                                    <td class='text-end'>{formatNumber(r.qty)}</td>
                                                    <td class='text-end'>{formatCurrency(r.price)}</td>
                                                    <td class='text-end'>{formatNumber(r.discountPct)}</td>
                                                    <td class='text-end'>{formatCurrency(r.flatDiscount)}</td>
                                                    <td class='text-end'>{formatCurrency(r.lineNet)}</td>
                                                    <td class='text-end'>{formatNumber(r.vatPct)}</td>
                                                    <td class='text-end'>{formatCurrency(r.lineVat)}</td>
                                                    <td class='text-end font-weight-bold'>{formatCurrency(r.lineGross)}</td>
                                                </tr>
                                            </>);
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Optional: timeline summary if you have monthly buckets
                            {timeline && timeline.length > 0 && (
                                <div class='mb-3'>
                                    <h6 class='mb-2'>Monthly totals</h6>
                                    <div class='table-responsive'>
                                        <table class='table table-sm'>
                                            <thead>
                                                <tr>
                                                    <th>Month</th>
                                                    <th class='text-end'>Units</th>
                                                    <th class='text-end'>Net</th>
                                                    <th class='text-end'>VAT</th>
                                                    <th class='text-end'>Gross</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {timeline.map((t) => (
                                                    <tr key={String(t.month)}>
                                                        <td>{formatDateTime(t.month)}</td>
                                                        <td class='text-end'>{formatNumber(t.units)}</td>
                                                        <td class='text-end'>{formatCurrency(t.netRevenue)}</td>
                                                        <td class='text-end'>{formatCurrency(t.vatCollected)}</td>
                                                        <td class='text-end'>{formatCurrency(t.grossRevenue)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )} */}

                            {versions && versions.length > 0 && (
                                <div class='mb-3'>
                                    <h6 class='mb-2'><Text id='product.purchase-details-versions'>Versions</Text></h6>
                                    <div class='table-responsive'>
                                        <table class='table table-sm'>
                                            <thead>
                                                <tr>
                                                    <th style='white-space: nowrap;'><Text id='product.purchase-details-version-date'>Date</Text></th>
                                                    <th style='white-space: nowrap;'><Text id='product.purchase-details-version-user'>User</Text></th>
                                                    <th><Text id='product.purchase-details-version-changes'>Changes</Text></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {versions.map((v) => (
                                                    <tr key={v.id}>
                                                        <td style='white-space: nowrap;'>{util.formatDate(v.createdDate, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}</td>
                                                        <td style='white-space: nowrap;'>{v.userObject?.firstname || '-'} {v.userObject?.lastname || ''}</td>
                                                        <td>
                                                            {v.data && Object.keys(v.data).length > 0 ? (
                                                                <ul class='mb-0'>
                                                                    {Object.entries(v.data).map(([key, change]) => (
                                                                        <li key={key}>
                                                                            <strong>{key}:</strong> &rarr; {util.isObject(change) ? JSON.stringify(change) : String(change)}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : <span class='text-muted'><Text id='product.purchase-details-no-changes'>No changes recorded</Text></span>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Debug: raw json */}
                            <details>
                                <summary class='cursor-pointer'>Raw JSON</summary>
                                <pre class='mt-2 small'>{JSON.stringify(purchaseDetails, null, 4)}</pre>
                            </details>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}
export default ProductPurchaseDetails;